import { Close, ExclamationCircle } from 'flowbite-react-icons/outline'
import type { EhrDemographicsFieldDiff } from '@/pages/order/ehrDemographicsDrift'

export interface EhrDemographicsUpdateModalProps {
  isOpen: boolean
  /** Dismiss without sending (keep the saved details). */
  onClose: () => void
  /** Push the EHR demographics to the lab (runs after user confirms). */
  onConfirm: () => void | Promise<void>
  /** The fields whose EHR value differs from the saved order value. */
  changedFields: EhrDemographicsFieldDiff[]
  /** True while the update is being sent to the lab. */
  isSubmitting?: boolean
}

/**
 * Surfaced when a submitted order is reopened from an Epic launch and the
 * proband's EHR demographics (name, DOB, sex, MRN) no longer match what was
 * saved on the order. Shows each changed field side-by-side (saved → EHR) and
 * offers to send the corrected values to the lab. Mirrors the visual language
 * of {@link SendOrderUpdatesConfirmModal}.
 */
export function EhrDemographicsUpdateModal({
  isOpen,
  onClose,
  onConfirm,
  changedFields,
  isSubmitting = false,
}: EhrDemographicsUpdateModalProps) {
  if (!isOpen || changedFields.length === 0) {
    return null
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center px-4 py-6">
      <button
        type="button"
        aria-label="Close dialog"
        onClick={() => {
          if (!isSubmitting) onClose()
        }}
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
      />
      <div
        className="relative w-full max-w-md rounded-base border border-border-base bg-white p-6 shadow-lg dark:border-gray-700 dark:bg-gray-900"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ehr-demographics-update-title"
      >
        <button
          type="button"
          onClick={onClose}
          disabled={isSubmitting}
          aria-label="Close"
          className="absolute right-3 top-3 flex size-9 items-center justify-center rounded-full text-text-body transition-colors hover:bg-background-gray hover:text-text-heading dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white"
        >
          <Close className="h-5 w-5" />
        </button>

        <div className="flex flex-col items-center gap-4 pt-1">
          <div className="flex size-12 shrink-0 items-center justify-center text-text-fg-brand" aria-hidden="true">
            <ExclamationCircle className="h-10 w-10" />
          </div>

          <div className="w-full text-center text-text-body dark:text-gray-400">
            <p
              id="ehr-demographics-update-title"
              className="text-lg font-semibold tracking-tight text-text-heading dark:text-white"
            >
              Patient details changed in the EHR
            </p>

            <p className="mt-2 text-base font-medium leading-6 text-text-body dark:text-gray-400">
              The patient&apos;s demographics in the EHR no longer match the details saved on this
              order. Send the updated details to the lab?
            </p>

            <ul className="mt-4 flex flex-col gap-3 text-left">
              {changedFields.map((diff) => (
                <li
                  key={diff.field}
                  className="rounded-base border border-border-base bg-background-gray px-4 py-3 dark:border-gray-700 dark:bg-gray-800"
                >
                  <p className="text-xs font-medium uppercase tracking-wide text-text-body-subtle dark:text-gray-400">
                    {diff.label}
                  </p>
                  <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-base font-medium">
                    <span className="text-text-body line-through dark:text-gray-400">
                      {diff.savedValue || '—'}
                    </span>
                    <span aria-hidden="true" className="text-text-body-subtle dark:text-gray-500">
                      →
                    </span>
                    <span className="text-text-heading dark:text-white">{diff.ehrValue || '—'}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-2 flex w-full flex-col-reverse gap-3 sm:flex-row sm:justify-center">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="inline-flex min-h-[2.5rem] flex-1 items-center justify-center rounded-full border border-border-base bg-white px-4 py-2.5 text-sm font-medium text-text-body shadow-xs transition-colors hover:bg-background-gray disabled:cursor-not-allowed disabled:opacity-50 sm:flex-initial sm:min-w-[10rem] dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400 dark:hover:bg-gray-800"
            >
              Keep saved details
            </button>
            <button
              type="button"
              onClick={() => void onConfirm()}
              disabled={isSubmitting}
              className="inline-flex min-h-[2.5rem] flex-1 items-center justify-center rounded-full border border-transparent bg-brand-600 px-4 py-2.5 text-sm font-medium text-white shadow-xs transition-colors hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50 sm:flex-initial sm:min-w-[10rem]"
            >
              {isSubmitting ? 'Sending…' : 'Send update to lab'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
